import { Button, Link, Stack, TextField, Typography } from "@mui/material";
import { IconButton, InputAdornment } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FormBox } from "@pages/auth/login/Login.style";
import { PATHS } from "config/paths";
import { useFormik } from "formik";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLoginMutation } from "store/auth/auth.api";
import * as Yup from "yup";

const LoginForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [login, { isLoading }] = useLoginMutation();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      remember: false,
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("login.email_invalid", "Email invalide"))
        .required(t("login.email_required", "Email requis")),
      password: Yup.string()
        .min(6, t("login.password_min", "Minimum 6 caractères"))
        .required(t("login.password_required", "Mot de passe requis")),
    }),
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      try {
        const result = await login({
          email: values.email,
          password: values.password,
        }).unwrap();

        toast.success(result.data?.message || "Connexion réussie");

        navigate("/confirm-email", { state: { email: values.email } });
      } catch (error) {
        console.error("Login failed:", error);
        setErrors({
          password: t(
            "login.invalid_credentials",
            "Email ou mot de passe incorrect"
          ),
        });

        toast.error(t("login.invalid_credentials", "Identifiants invalides"));
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <FormBox component="form" onSubmit={formik.handleSubmit}>
      <Stack spacing={2}>
        <Typography variant="body1" sx={{ color: "#0E75E2" }}>
          {t("login.email_label")}
        </Typography>
        <TextField
          fullWidth
          id="email"
          name="email"
          placeholder={t("login.email_label")}
          variant="standard"
          value={formik.values.email}
          onChange={formik.handleChange}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />

        <Typography variant="body1" sx={{ color: "#0E75E2" }}>
          {t("login.password_label")}
        </Typography>
        <TextField
          fullWidth
          id="password"
          name="password"
          type={showPassword ? "text" : "password"}
          placeholder={t("login.password_label")}
          variant="standard"
          value={formik.values.password}
          onChange={formik.handleChange}
          error={formik.touched.password && Boolean(formik.errors.password)}
          helperText={formik.touched.password && formik.errors.password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword} edge="end">
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center">
          <Link
            href={PATHS.FORGOT_PASSWORD}
            underline="hover"
            sx={{ color: "#0E75E2", fontSize: "11px" }}>
            {t("login.forgot_password") + " ?"}
          </Link>
        </Stack>

        <Stack sx={{ display: "flex", alignItems: "end" }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            sx={{
              backgroundColor: "#0F84FF",
              color: "#fff",
              textTransform: "none",
              fontWeight: "bold",
              width: "45%",
              "&:hover": {
                backgroundColor: "#094F99",
              },
            }}>
            {t("login.btn_signin")}
          </Button>
        </Stack>
      </Stack>
    </FormBox>
  );
};

export default LoginForm;
